import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";

function round2(n) { return Math.round(n * 100) / 100; }
function uid() {
  try { return crypto.randomUUID(); } catch { return `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; }
}

/** Auto-compute the price for a single position or sub-position.  A parent
 *  work-item that owns sub_items is always the sum of its children — user
 *  overrides on a parent with children are ignored. */
export function autoCalcPrice(item, defaultRate = 100) {
  const subs = item?.sub_items || [];
  if (subs.length > 0) {
    return round2(subs.reduce((s, sub) => s + autoCalcPrice(sub, defaultRate), 0));
  }
  if (item.price_manual) return Number(item.price) || 0;
  const type = item.type || "labor";  // legacy items may have no type set
  switch (type) {
    case "labor": {
      const hRaw = item.hours;
      const h = Number(hRaw) || 0;
      const r = Number(item.hourly_rate ?? defaultRate) || 0;
      // Preserve manually-entered legacy totals: if hours was never set
      // (nullish, not zero) fall back to the persisted price so we don't
      // wipe historical values just for opening an old contract.
      if ((hRaw === null || hRaw === undefined) && item.price) {
        return Number(item.price) || 0;
      }
      return round2(h * r);
    }
    case "part": {
      const qRaw = item.quantity;
      const upRaw = item.unit_price;
      const q = Number(qRaw) || 0;
      const p = Number(upRaw) || 0;
      if ((qRaw === null || qRaw === undefined) &&
          (upRaw === null || upRaw === undefined) &&
          item.price) {
        return Number(item.price) || 0;
      }
      return round2(q * p);
    }
    case "flat":
    case "other":
    default:
      return Number(item.price) || 0;
  }
}

/** Compute {labor, parts, flat, other, grand} totals from a work_items array.
 *  Sub-positions are categorised by *their own* type so an "Oil change" parent
 *  correctly reports mixed labour + parts subtotals. */
export function computeTotals(items, defaultRate = 100) {
  let labor = 0, parts = 0, flat = 0, other = 0;
  const walk = (it) => {
    const subs = it?.sub_items || [];
    if (subs.length > 0) {
      subs.forEach(walk);
      return;
    }
    const p = autoCalcPrice(it, defaultRate);
    const type = it.type || "labor";  // legacy items default to labor
    if (type === "part") parts += p;
    else if (type === "flat") flat += p;
    else if (type === "other") other += p;
    else labor += p;
  };
  (items || []).forEach(walk);
  const grand = round2(labor + parts + flat + other);
  return { labor: round2(labor), parts: round2(parts), flat: round2(flat), other: round2(other), grand };
}

/** Total labor working time of a contract, in HOURS.
 *  Walks parent items AND sub-items recursively.  Only labor-type leaves
 *  contribute.  Precedence per item:
 *    1. explicit `hours` field (new pricing model)
 *    2. legacy `aw` field (10 AW = 1 h) so historical contracts keep their value
 *    3. 0 otherwise
 *  Non-labor items (part / flat / other) contribute 0 — they do not consume
 *  workshop time.  A parent that owns sub-items delegates entirely to its
 *  children so we never double-count.
 */
export function computeTotalHours(items) {
  let hours = 0;
  const walk = (it) => {
    const subs = it?.sub_items || [];
    if (subs.length > 0) { subs.forEach(walk); return; }
    const type = it?.type || "labor";  // legacy items default to labor
    if (type !== "labor") return;
    if (it.hours !== null && it.hours !== undefined && it.hours !== "") {
      hours += Number(it.hours) || 0;
    } else if (it.aw) {
      hours += (Number(it.aw) || 0) / 10;
    }
  };
  (items || []).forEach(walk);
  return Math.round(hours * 100) / 100;
}

/** Same value as computeTotalHours() but returned in AW (10 AW = 1 h).
 *  Kept as a FLOAT so quarter-hour precision is preserved end-to-end
 *  (1.25 h → 12.5 AW, not 13).  Integer rendering happens only in the
 *  "aw" display mode inside formatWorkTime. */
export function computeTotalWorkTimeAw(items) {
  return Math.round(computeTotalHours(items) * 1000) / 100;  // hours * 10, 2-dec precision
}

/** VAT-aware totals: net + vat + gross for the whole tree.
 *  Every leaf uses its own `vat_rate` (fallback: `defaultVat`).
 *  Non-labor/part/flat items contribute nothing. */
export function computeTotalsVat(items, defaultRate = 100, defaultVat = 0) {
  let net = 0, vat = 0;
  const walk = (it) => {
    const subs = it?.sub_items || [];
    if (subs.length > 0) { subs.forEach(walk); return; }
    const p = autoCalcPrice(it, defaultRate);
    const rate = Number(it?.vat_rate ?? defaultVat) || 0;
    net += p;
    vat += p * rate / 100;
  };
  (items || []).forEach(walk);
  net = round2(net);
  vat = round2(vat);
  return { net, vat, gross: round2(net + vat) };
}

/** Create a new blank sub-position row. */
function makeBlankSub(defaultRate) {
  return {
    id: uid(),
    type: "labor",
    description: "",
    hours: 1,
    hourly_rate: defaultRate,
    price: defaultRate,
    price_manual: false,
    notes: "",
  };
}

/**
 * Top-level work item row.  Supports optional sub-positions:
 *   • when `sub_items.length > 0` the parent renders as a *container*: the
 *     detail row is hidden and the total is auto-summed from children;
 *   • when empty the parent behaves like a single line item (labor / part /
 *     flat / other) exactly as before.
 * The `onChange` callback always receives the full updated item.
 */
export default function WorkItemRow({
  item, idx, disabled, defaultRate = 100,
  vatEnabled = false, pricingEnabled = true, defaultVat = 0, vatOptions = [0, 19],
  onChange, onToggle, onDelete,
}) {
  const { t } = useTranslation();
  const type = item.type || "labor";
  const subs = item.sub_items || [];
  const hasSubs = subs.length > 0;
  const price = autoCalcPrice(item, defaultRate);
  const [expanded, setExpanded] = useState(true);
  const patch = (fields) => onChange({ ...item, ...fields });

  const setPriceManual = (v) =>
    patch({ price: Number(v) || 0, price_manual: true });

  const addSub = () => {
    const next = [...subs, makeBlankSub(defaultRate)];
    onChange({ ...item, sub_items: next, price_manual: false });
    setExpanded(true);
  };

  const updateSub = (subIdx, nextSub) => {
    const nextArr = subs.map((s, i) => i === subIdx ? nextSub : s);
    onChange({ ...item, sub_items: nextArr, price_manual: false });
  };

  const removeSub = (subIdx) => {
    const nextArr = subs.filter((_, i) => i !== subIdx);
    onChange({ ...item, sub_items: nextArr, price_manual: false });
  };

  return (
    <div className="border border-[#2E3241] rounded-lg p-3 space-y-2" data-testid={`work-item-${idx}`}>
      {/* Header row: type + description + total + actions */}
      <div className="flex items-center gap-2">
        <Checkbox checked={!!item.completed} onCheckedChange={(v) => onToggle(!!v)}
          data-testid={`work-checkbox-${idx}`} className="shrink-0" />
        {hasSubs && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-zinc-400 hover:text-zinc-200 shrink-0"
            data-testid={`work-expand-${idx}`}
            aria-label="toggle sub-positions"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        )}
        <Select value={type}
          onValueChange={(v) => patch({ type: v, price_manual: false })}
          disabled={disabled}>
          <SelectTrigger className="bg-[#171B24] border-[#2E3241] w-32 h-9 shrink-0" data-testid={`work-type-${idx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
            <SelectItem value="labor">{t("workitem.labor")}</SelectItem>
            <SelectItem value="part">{t("workitem.part")}</SelectItem>
            <SelectItem value="flat">{t("workitem.flat")}</SelectItem>
            <SelectItem value="other">{t("workitem.other")}</SelectItem>
          </SelectContent>
        </Select>
        <Input value={item.description || ""}
          onChange={(e) => patch({ description: e.target.value })}
          disabled={disabled}
          placeholder={t("common.description")}
          className="bg-[#171B24] border-[#2E3241] flex-1"
          data-testid={`work-desc-${idx}`} />
        {pricingEnabled && vatEnabled && !hasSubs && (
          <Select value={String(item.vat_rate ?? defaultVat)}
            onValueChange={(v) => patch({ vat_rate: Number(v) })}
            disabled={disabled}>
            <SelectTrigger className="bg-[#171B24] border-[#2E3241] w-[80px] h-9 shrink-0" data-testid={`work-vat-${idx}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
              {vatOptions.map((v) => (
                <SelectItem key={v} value={String(v)}>{v}%</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {pricingEnabled && (
          <div className="font-mono-stat text-sm text-emerald-300 min-w-[80px] text-right"
            data-testid={`work-item-total-${idx}`}>
            {price.toFixed(2)} €
          </div>
        )}
        {!disabled && (
          <Button variant="ghost" size="icon" onClick={addSub}
            className="text-zinc-400 hover:text-blue-400 shrink-0"
            title={t("workitem.add_sub_item")}
            data-testid={`work-add-sub-${idx}`}>
            <Plus className="h-4 w-4" />
          </Button>
        )}
        <Button variant="ghost" size="icon" onClick={onDelete} disabled={disabled}
          className="text-zinc-400 hover:text-red-400 shrink-0" data-testid={`work-delete-${idx}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Type-specific detail row — only when the item has NO sub-items */}
      {!hasSubs && type === "labor" && !disabled && (
        <div className="grid grid-cols-12 gap-2 pl-8 items-center text-sm">
          <NumInput label={t("common.hours")} span={3}
            value={item.hours ?? ""} step="0.25"
            onChange={(v) => patch({ hours: v, price_manual: false })}
            testid={`work-hours-${idx}`} />
          <NumInput label={`€/${t("common.hour_short")}`} span={3}
            value={item.hourly_rate ?? defaultRate} step="0.01"
            onChange={(v) => patch({ hourly_rate: v, price_manual: false })}
            testid={`work-rate-${idx}`} />
          {pricingEnabled ? (
            <div className="col-span-6">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{t("common.total")} €</div>
              <Input type="number" step="0.01"
                value={price}
                onChange={(e) => setPriceManual(e.target.value)}
                className="bg-[#171B24] border-[#2E3241] font-mono-stat h-9"
                data-testid={`work-price-${idx}`} />
            </div>
          ) : <div className="col-span-6" />}
        </div>
      )}

      {!hasSubs && type === "part" && !disabled && (
        <div className="grid grid-cols-12 gap-2 pl-8 items-center text-sm">
          <NumInput label={t("common.qty")} span={2}
            value={item.quantity ?? ""} step="0.01"
            onChange={(v) => patch({ quantity: v, price_manual: false })}
            testid={`work-qty-${idx}`} />
          <div className="col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{t("common.unit")}</div>
            <Input value={item.unit ?? ""}
              onChange={(e) => patch({ unit: e.target.value })}
              placeholder="pcs / l / kg"
              className="bg-[#171B24] border-[#2E3241] h-9"
              data-testid={`work-unit-${idx}`} />
          </div>
          {pricingEnabled && (
            <>
              <NumInput label={t("common.unit_price") + " €"} span={3}
                value={item.unit_price ?? ""} step="0.01"
                onChange={(v) => patch({ unit_price: v, price_manual: false })}
                testid={`work-unitprice-${idx}`} />
              <div className="col-span-5">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{t("common.total")} €</div>
                <Input type="number" step="0.01"
                  value={price}
                  onChange={(e) => setPriceManual(e.target.value)}
                  className="bg-[#171B24] border-[#2E3241] font-mono-stat h-9"
                  data-testid={`work-price-${idx}`} />
              </div>
            </>
          )}
        </div>
      )}

      {!hasSubs && (type === "flat" || type === "other") && !disabled && pricingEnabled && (
        <div className="grid grid-cols-12 gap-2 pl-8 items-center text-sm">
          <div className="col-span-12 md:col-span-5">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{t("workitem.fixed_price")} €</div>
            <Input type="number" step="0.01"
              value={item.price ?? 0}
              onChange={(e) => patch({ price: Number(e.target.value) || 0, price_manual: true })}
              className="bg-[#171B24] border-[#2E3241] font-mono-stat h-9"
              data-testid={`work-price-${idx}`} />
          </div>
        </div>
      )}

      {/* Sub-positions */}
      {hasSubs && expanded && (
        <div className="pl-8 space-y-2 border-l-2 border-[#2A2E38] ml-3">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
            <span>{t("workitem.sub_items")} · <span className="normal-case tracking-normal text-zinc-500">{t("workitem.auto_from_sub")}</span></span>
            <span className="font-mono-stat text-zinc-400">{t("workitem.sub_total")}: <span className="text-emerald-300">{price.toFixed(2)} €</span></span>
          </div>
          {subs.map((sub, si) => (
            <SubItemRow
              key={sub.id || si}
              parentIdx={idx}
              subIdx={si}
              item={sub}
              defaultRate={defaultRate}
              disabled={disabled}
              onChange={(next) => updateSub(si, next)}
              onDelete={() => removeSub(si)}
            />
          ))}
          {!disabled && (
            <Button type="button" variant="ghost" size="sm" onClick={addSub}
              className="text-blue-400 hover:bg-[#2A2F3A]"
              data-testid={`work-add-sub-inline-${idx}`}>
              <Plus className="h-3 w-3 mr-1" /> {t("workitem.add_sub_item")}
            </Button>
          )}
        </div>
      )}

      {item.notes || !disabled ? (
        <Input value={item.notes || ""}
          onChange={(e) => patch({ notes: e.target.value })}
          disabled={disabled}
          placeholder={t("common.notes")}
          className="bg-[#171B24] border-[#2E3241] h-8 text-xs ml-8"
          data-testid={`work-notes-${idx}`} />
      ) : null}
    </div>
  );
}

/** A single sub-position row — compact, one line, type-dependent inputs. */
function SubItemRow({ parentIdx, subIdx, item, defaultRate, disabled, onChange, onDelete }) {
  const { t } = useTranslation();
  const type = item.type || "labor";
  const price = autoCalcPrice(item, defaultRate);
  const patch = (fields) => onChange({ ...item, ...fields });
  const tid = (name) => `sub-${parentIdx}-${subIdx}-${name}`;

  return (
    <div className="grid grid-cols-12 gap-2 items-center" data-testid={`sub-item-${parentIdx}-${subIdx}`}>
      <div className="col-span-2">
        <Select value={type}
          onValueChange={(v) => patch({ type: v, price_manual: false })}
          disabled={disabled}>
          <SelectTrigger className="bg-[#171B24] border-[#2E3241] h-9" data-testid={tid("type")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
            <SelectItem value="labor">{t("workitem.labor")}</SelectItem>
            <SelectItem value="part">{t("workitem.part")}</SelectItem>
            <SelectItem value="flat">{t("workitem.flat")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Input value={item.description || ""}
        onChange={(e) => patch({ description: e.target.value })}
        disabled={disabled}
        placeholder={t("common.description")}
        className="bg-[#171B24] border-[#2E3241] col-span-4 h-9"
        data-testid={tid("desc")} />

      {type === "labor" && (
        <>
          <NumInput span={2} label={t("common.hours")}
            value={item.hours ?? ""} step="0.25" disabled={disabled}
            onChange={(v) => patch({ hours: v, price_manual: false })}
            testid={tid("hours")} />
          <NumInput span={2} label={`€/${t("common.hour_short")}`}
            value={item.hourly_rate ?? defaultRate} step="0.01" disabled={disabled}
            onChange={(v) => patch({ hourly_rate: v, price_manual: false })}
            testid={tid("rate")} />
        </>
      )}
      {type === "part" && (
        <>
          <NumInput span={2} label={t("common.qty")}
            value={item.quantity ?? ""} step="0.01" disabled={disabled}
            onChange={(v) => patch({ quantity: v, price_manual: false })}
            testid={tid("qty")} />
          <NumInput span={2} label={t("common.unit_price") + " €"}
            value={item.unit_price ?? ""} step="0.01" disabled={disabled}
            onChange={(v) => patch({ unit_price: v, price_manual: false })}
            testid={tid("unitprice")} />
        </>
      )}
      {(type === "flat" || type === "other") && (
        <div className="col-span-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{t("workitem.fixed_price")} €</div>
          <Input type="number" step="0.01"
            value={item.price ?? 0}
            onChange={(e) => patch({ price: Number(e.target.value) || 0, price_manual: true })}
            disabled={disabled}
            className="bg-[#171B24] border-[#2E3241] font-mono-stat h-9"
            data-testid={tid("price")} />
        </div>
      )}

      <div className="col-span-1 font-mono-stat text-sm text-emerald-300 text-right"
        data-testid={tid("total")}>
        {price.toFixed(2)}
      </div>
      <div className="col-span-1 flex justify-end">
        {!disabled && (
          <Button variant="ghost" size="icon" onClick={onDelete}
            className="text-zinc-400 hover:text-red-400 h-8 w-8"
            data-testid={tid("delete")}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

function NumInput({ label, span, value, step, onChange, testid, disabled }) {
  return (
    <div className={`col-span-${span}`}>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <Input type="number" step={step} disabled={disabled}
        value={value === null || value === undefined ? "" : value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? null : (parseFloat(v) || 0));
        }}
        className="bg-[#171B24] border-[#2E3241] font-mono-stat h-9"
        data-testid={testid} />
    </div>
  );
}
